import type { ErrorInfo, ReactNode } from 'react'
import { Component } from 'react'
import { Button } from './Button'

export interface ErrorBoundaryProps {
  children: ReactNode
  /** Render your own fallback. `reset` re-mounts the subtree. */
  fallback?: (error: Error, reset: () => void) => ReactNode
  /** Changing this value clears a caught error — pass the current route. */
  resetKey?: unknown
  onError?: (error: Error, info: ErrorInfo) => void
}

interface State {
  error: Error | null
}

/**
 * A crash in one mode must not take the shell down with it: the user may be mid-shop with
 * a basket that only exists on this device, and they still need the other tab and export.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.props.onError?.(error, info)
    console.error('[resupport] unhandled error', error, info.componentStack)
  }

  override componentDidUpdate(prev: ErrorBoundaryProps): void {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null })
  }

  reset = () => this.setState({ error: null })

  override render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children
    if (this.props.fallback) return this.props.fallback(error, this.reset)

    return (
      <div className="rs-boundary" role="alert">
        <h2>Something broke</h2>
        <p>
          Your trips and basket are stored on this device and are unaffected. Try again, or go to
          another tab.
        </p>
        <pre>{error.message}</pre>
        <Button variant="primary" onClick={this.reset}>
          Try again
        </Button>
      </div>
    )
  }
}
